import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from '../common/Card';
import { PlayerStatRow, PlayerStatHeader } from './PlayerStatRow';
import type { MyStatsPerson } from '../../types';

// Ids and comparators mirror the per-ranking dashboard's opponent sort, so the
// same label means the same thing in both places.
type PeopleSort = 'matches' | 'bestWinRate' | 'worstWinRate';

const SORT_OPTIONS: Array<{ id: PeopleSort; label: string }> = [
  { id: 'matches', label: '+ Jogos' },
  { id: 'bestWinRate', label: '+ Vitórias' },
  { id: 'worstWinRate', label: '+ Derrotas' },
];

const LIMITS = [5, 10, 20] as const;

interface PeopleBreakdownCardProps {
  title: string;
  subtitle: string;
  /** Column heading for the name column, e.g. "Parceiro". */
  columnLabel: string;
  icon: ReactNode;
  iconTint: string;
  people: MyStatsPerson[];
}

/**
 * A sortable, length-adjustable partner/opponent breakdown.
 *
 * Sorting happens over the full list the API returned (not over an already
 * truncated slice), so "+ Vitórias" really is the best win rates and not just
 * the best among the most-played.
 */
export function PeopleBreakdownCard({
  title,
  subtitle,
  columnLabel,
  icon,
  iconTint,
  people,
}: PeopleBreakdownCardProps) {
  const [sort, setSort] = useState<PeopleSort>('matches');
  const [limit, setLimit] = useState<number>(5);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const sorted = [...people].sort((a, b) => {
    switch (sort) {
      case 'bestWinRate':
        return b.winRate - a.winRate || b.matches - a.matches;
      case 'worstWinRate':
        return a.winRate - b.winRate || b.matches - a.matches;
      case 'matches':
      default:
        return b.matches - a.matches || b.winRate - a.winRate;
    }
  });
  const visible = sorted.slice(0, limit);

  return (
    <Card className="!p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${iconTint}`}
          >
            {icon}
          </div>
          <div>
            <p className="text-white font-medium">{title}</p>
            <p className="text-white/50 text-sm">{subtitle}</p>
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            aria-label="Ordenar"
            className={`p-1.5 rounded-lg transition-colors ${
              showSortMenu
                ? 'bg-turquoise/20 text-turquoise'
                : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70'
            }`}
          >
            <ArrowUpDown className="w-4 h-4" />
          </button>
          {showSortMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
              <div className="absolute right-0 top-full mt-1 z-20 bg-[#1a1a2e] border border-white/15 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSort(option.id);
                      setShowSortMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      sort === option.id
                        ? 'bg-turquoise/15 text-turquoise font-medium'
                        : 'text-white/70 hover:bg-white/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <PlayerStatHeader label={columnLabel} />
        <AnimatePresence mode="popLayout">
          {visible.map((person, index) => (
            <motion.div
              key={person.subscriberId}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, delay: index * 0.02 }}
            >
              <PlayerStatRow {...person} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Only offer lengths the data can actually fill. */}
      {people.length > LIMITS[0] && (
        <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-white/5">
          <span className="text-xs text-white/40 mr-auto">Mostrar</span>
          {LIMITS.filter((_, i) => i === 0 || people.length > LIMITS[i - 1]).map((n) => (
            <button
              key={n}
              onClick={() => setLimit(n)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                limit === n
                  ? 'bg-turquoise/20 text-turquoise'
                  : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
